import { AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { CategoryErrorKey, StoreCategoryWithMedia, TranslationKey } from '@dukanti/shared';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

export interface DeleteCategoryDialogProps {
  readonly category: StoreCategoryWithMedia | null;
  readonly open: boolean;
  readonly isDeleting?: boolean;
  readonly errorKey?: CategoryErrorKey | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: () => void;
}

export function DeleteCategoryDialog({
  category,
  open,
  isDeleting = false,
  errorKey,
  onOpenChange,
  onConfirm,
}: DeleteCategoryDialogProps) {
  const { t, dir } = useTranslation();
  const name = category?.name ?? '';

  return (
    <AlertDialog open={open} onOpenChange={(next) => !isDeleting && onOpenChange(next)}>
      <AlertDialogContent dir={dir} data-testid="delete-category-dialog">
        <div className="bg-terracotta/10 text-terracotta mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
          <Trash2 className="size-6" aria-hidden />
        </div>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center">
            {t('admin.categories.delete.title')}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {t('admin.categories.delete.description', { name })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {errorKey ? (
          <div
            role="alert"
            data-error-key={errorKey}
            className="border-terracotta/30 bg-terracotta/10 text-sand-700 rounded-card flex items-start gap-2 border p-3 text-start text-sm"
          >
            <AlertCircle
              aria-hidden="true"
              className="text-terracotta mt-0.5 size-4 shrink-0"
              strokeWidth={1.75}
            />
            <div>
              <p>{t(errorKey as TranslationKey)}</p>
              {errorKey === 'admin.categories.errors.assignedProducts' ? (
                <p className="mt-1">{t('admin.categories.delete.assignedProductsRecovery')}</p>
              ) : null}
            </div>
          </div>
        ) : null}

        <AlertDialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={isDeleting}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isDeleting || !category}
            onClick={onConfirm}
            aria-busy={isDeleting || undefined}
          >
            {isDeleting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span>{t('admin.categories.delete.confirm')}</span>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
